import * as React from 'react';
import { observer } from 'mobx-react';

import {
  usePermissionsStore,
  useProjectStore,
  useRouterStore,
  useUiStore
} from 'stores/index';
import { useLocalStore } from 'shared/entities/common/hooks';
import LazySuspense from 'shared/components/LazySuspense';
import LoaderSpinner from 'shared/newComponents/animations/LoaderSpinner';
import {
  LoaderSpinnerColor,
  LoaderSpinnerSize
} from 'shared/newEntities/components/LoaderSpinner';
import ELamaPopup from 'shared/components/ELamaPopup';
import {
  PageTemplateMenuStore,
  PageTemplateMenuStoreProvider
} from 'stores/PageTemplateMenuStore';

import Left from './Left';
import Header from './Header';
import MobileMenu from './MobileMenu';
import TopMenu from './TopMenu';

import './PageTemplate.modules.scss';

type Props = {
  children: React.ReactNode;
};

const PageTemplate: React.FC<Props> = ({ children }: Props) => {
  const uiStore = useUiStore();
  const projectStore = useProjectStore();
  const permissionsStore = usePermissionsStore();
  const routerStore = useRouterStore();

  const pageTemplateMenuStoreCtx = useLocalStore(
    () =>
      new PageTemplateMenuStore({
        uiStore,
        routerStore,
        permissionsStore
      })
  );

  const { store: menuStore } = pageTemplateMenuStoreCtx;

  React.useEffect(() => {
    menuStore.setSections(projectStore.projectId);
  }, [menuStore, projectStore.projectId]);

  return (
    <PageTemplateMenuStoreProvider value={pageTemplateMenuStoreCtx}>
      <div
        styleName="page-template"
        className={
          menuStore.isMenuOpened
            ? 'page-template_menu-opened'
            : 'page-template_menu-closed'
        }
      >
        {!menuStore.isMobileMenu && <Left />}
        {menuStore.isMobileMenu && (
          <Header
            onBurgerClick={menuStore.toggleMenu}
            styleName="page-template__header"
          />
        )}
        <div styleName="page-template__content">
          {menuStore.isMobileMenu && <MobileMenu />}
          <TopMenu />
          <div styleName="page-template__content-page">
            <LazySuspense
              fallback={
                <LoaderSpinner
                  color={LoaderSpinnerColor.blue}
                  centered
                  size={LoaderSpinnerSize.md}
                />
              }
            >
              {children}
            </LazySuspense>
            {permissionsStore.isELamaUser && (
              <ELamaPopup styleName="page-template__elama" />
            )}
          </div>
        </div>
      </div>
    </PageTemplateMenuStoreProvider>
  );
};

export default observer(PageTemplate);
